/**
 * Principal-Based Recalculation Engine - Comprehensive recalculation after principal adjustments
 *
 * This module provides a general-purpose recalculation engine that:
 * - Assumes all principal amounts are already correctly set
 * - Recalculates all interest, obligations, and related fields
 * - Works backwards from last to first record (like revenue distributor)
 * - Can be used after any manual principal adjustment (five-year rule, manual edits, etc.)
 *
 * This is more powerful than surgical updates as it comprehensively recalculates
 * everything based on the current principal distribution.
 */

import { logger } from '~/services/logging';
import type { BondSeries, Candidate, CashFlow } from '~/types';
import { InterestCalculationEngine } from '../interest-engine/interest-calculator';
import { InterestCascadingEngine } from '../interest-engine/interest-cascader';

export class PrincipalBasedRecalculationEngine {
  /**
   * Main recalculation function - assumes principal is correct and recalculates everything else
   * Works backwards like the revenue distributor to handle cascading properly
   *
   * Critical sequencing: Must calculate totalInterest BEFORE cascading to ensure
   * correct values are propagated backwards to prior payment periods
   *
   * @param candidate - The candidate to recalculate
   * @param bondSeries - Bond series configuration (from configuration.bondSeries)
   */
  static recalculateFromPrincipal(candidate: Candidate, bondSeries: BondSeries[]): void {
    const { cashFlows } = candidate;

    logger.L4.info('🔄 Starting principal-based recalculation');
    logger.L4.info(`   Candidate: ${candidate.name}`);
    logger.L4.info(`   Total cashflows: ${cashFlows.length}`);

    // Backwards loop - critical for proper interest cascading
    for (let i = cashFlows.length - 1; i >= 0; i--) {
      const recordInProcess = cashFlows[i];

      // Step 1: Recalculate new interest for all bonds in this period
      PrincipalBasedRecalculationEngine.recalculateNewInterestForRecord(
        recordInProcess,
        i,
        cashFlows,
        bondSeries
      );

      // Step 2: Calculate total interest for the current record BEFORE cascading
      // This is critical: we must calculate totalInterest before cascading so that
      // when updateFutureInterestObligations reads matchingPayment.totalInterest,
      // it gets the recalculated value based on the new principal amounts
      for (const bondPayment of recordInProcess.bondPayments) {
        bondPayment.totalInterest =
          bondPayment.newInterest + (bondPayment.futureInterestObligation || 0);
        bondPayment.totalPayment = bondPayment.principal + bondPayment.totalInterest;
      }

      // Step 3: Cascade interest backwards to all previous payment periods
      for (let j = 0; j < i; j++) {
        if (cashFlows[j].isActive) {
          InterestCascadingEngine.cascadeNewInterestBackwards(
            recordInProcess,
            cashFlows[j],
            bondSeries
          );

          // After cascading updates future obligations, recalculate total interest
          // This is necessary because cascading may have prorated the future obligations for first payments
          for (const bondPayment of cashFlows[j].bondPayments) {
            if (bondPayment.isActive) {
              bondPayment.totalInterest =
                bondPayment.newInterest + (bondPayment.futureInterestObligation || 0);
              bondPayment.totalPayment = bondPayment.principal + bondPayment.totalInterest;
            }
          }

          // Update the record total after fixing bond totals
          PrincipalBasedRecalculationEngine.updateRecordTotals(cashFlows[j]);
        }
      }

      // Step 4: Update total bond payment for this record
      // (Total interest was already calculated before cascading in Step 2)
      PrincipalBasedRecalculationEngine.updateRecordTotals(recordInProcess);
    }

    logger.L4.info('✅ Completed principal-based recalculation');
  }

  /**
   * Recalculates new interest for all bond payments in a record
   * Uses the principal amount and calculates interest from previous payment date
   */
  private static recalculateNewInterestForRecord(
    record: CashFlow,
    recordIndex: number,
    cashFlows: CashFlow[],
    bondSeries: BondSeries[]
  ): void {
    // Get previous payment date for interest calculation
    const previousPaymentDate = recordIndex > 0 ? cashFlows[recordIndex - 1].paymentDate : null;

    // Recalculate interest for each bond payment
    for (const bondPayment of record.bondPayments) {
      if (!bondPayment.isActive) {
        // No interest on inactive bonds
        bondPayment.newInterest = 0;
        bondPayment.totalInterest = bondPayment.futureInterestObligation || 0;
        bondPayment.totalPayment = bondPayment.principal + bondPayment.totalInterest;
        continue;
      }

      // For interest-only payments (principal = 0), we still need to calculate interest
      // based on the outstanding principal balance that will be paid in future periods

      // Find the bond series for this payment
      const bondSeriesInfo = bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
      if (!bondSeriesInfo) {
        console.error(`Bond series not found: ${bondPayment.bondSeriesId}`);
        continue;
      }

      // Get payment map data for efficient calculation
      const paymentMapData = InterestCalculationEngine.getPaymentMapData(
        bondSeriesInfo,
        record.paymentDate
      );

      // Use the interest rate from payment map (includes escalation)
      const interestRate = paymentMapData.interestRate;

      // Calculate new interest - ALWAYS prefer pre-calculated days when available
      if (paymentMapData.daysToPrior !== undefined) {
        // Use pre-calculated days for ALL payments (including first payment)
        // This ensures consistency with how interest was originally calculated

        // For both interest-only and principal payments, we need to recalculate
        // the new interest based on the actual period length
        // The futureInterestObligation stays as-is (it's the accumulated amount from future periods)
        // We only recalculate the newInterest portion

        if (bondPayment.principal === 0 && bondPayment.futureInterestObligation > 0) {
          // Interest-only payment
          // The futureInterestObligation contains the cascaded interest from all future bonds
          // Now properly prorated by the cascading engine for first payments

          bondPayment.newInterest = 0; // No principal being paid, so no new interest

          // Trust the cascaded future obligation - it's already prorated if needed
        } else {
          // Principal payment - calculate interest on the principal being paid
          bondPayment.newInterest = Math.round(
            InterestCalculationEngine.calculateInterestFromDays(
              bondPayment.principal,
              interestRate,
              paymentMapData.daysToPrior
            )
          );
        }
      } else {
        // Fallback: determine start date and calculate
        // This should rarely happen as daysToPrior should always be populated
        const startDate = previousPaymentDate
          ? previousPaymentDate
          : bondSeriesInfo.issueDate ||
            PrincipalBasedRecalculationEngine.getEstimatedIssueDate(record.paymentDate);

        if (bondPayment.principal === 0 && bondPayment.futureInterestObligation > 0) {
          // Interest-only payment
          bondPayment.newInterest = 0;
          // Trust the cascaded future obligation - cascading handles first payment proration
        } else {
          // Principal payment
          bondPayment.newInterest = Math.round(
            InterestCalculationEngine.calculateInterestForPeriod(
              bondPayment.principal,
              interestRate,
              startDate,
              record.paymentDate
            )
          );
        }
      }

      // Don't calculate totalInterest here - wait until after cascading updates futureInterestObligation
      // This will be calculated after cascading in the main loop
    }
  }

  /**
   * Updates record-level totals after bond payment recalculation
   */
  private static updateRecordTotals(record: CashFlow): void {
    // Recalculate total bond payment
    record.totalBondPayment = record.bondPayments.reduce((sum, bp) => sum + bp.totalPayment, 0);

    // Interest obligation will be updated by cascading from future periods
    // Available for bonds will be recalculated based on new interest obligations
  }

  /**
   * Estimates issue date for first payment period interest calculation
   * Typically 30 days before first payment for municipal bonds
   */
  private static getEstimatedIssueDate(firstPaymentDate: string): string {
    const paymentDate = new Date(firstPaymentDate);
    const issueDate = new Date(paymentDate);
    issueDate.setDate(issueDate.getDate() - 30);

    return issueDate.toISOString().split('T')[0];
  }

  /**
   * Validation function to check if recalculation was successful
   */
  static validateRecalculation(cashFlows: CashFlow[]): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];

    for (let i = 0; i < cashFlows.length; i++) {
      const record = cashFlows[i];

      // Check that totals match sum of parts
      const calculatedTotal = record.bondPayments.reduce((sum, bp) => sum + bp.totalPayment, 0);
      if (Math.abs(record.totalBondPayment - calculatedTotal) > 0.01) {
        issues.push(
          `Record ${i}: Total bond payment mismatch. Expected ${calculatedTotal}, got ${record.totalBondPayment}`
        );
      }

      // Check that interest calculations are reasonable
      for (const bondPayment of record.bondPayments) {
        if (bondPayment.principal > 0 && bondPayment.newInterest === 0 && !record.isInterestOnly) {
          issues.push(
            `Record ${i}: Bond ${bondPayment.bondSeriesId} has principal but no new interest`
          );
        }

        if (bondPayment.totalInterest < bondPayment.newInterest) {
          issues.push(
            `Record ${i}: Bond ${bondPayment.bondSeriesId} total interest less than new interest`
          );
        }
      }

      // Check available for bonds calculation
      const expectedAvailable = record.revenueMinusExistingDebt - record.interestObligation;
      if (Math.abs(record.availableForBonds - expectedAvailable) > 0.01) {
        issues.push(
          `Record ${i}: Available for bonds mismatch. Expected ${expectedAvailable}, got ${record.availableForBonds}`
        );
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }
}
